import { useState } from 'react';
import { useMyCertificates } from '@/api/certificates';
import { PositionFilter } from '@/components/shared/position-filter';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Pagination } from '@/components/shared/pagination';
import { cn } from '@/lib/utils';
import { formatDate } from '@/lib/formatters';
import { Award, Download, Calendar } from 'lucide-react';
import { useCategoryLabel } from '@/api/categories';

const CATEGORY_COLORS: Record<string, string> = {
  induction: 'bg-blue-100 text-blue-700',
  periodic: 'bg-purple-100 text-purple-700',
  document_change: 'bg-amber-100 text-amber-700',
  general: 'bg-gray-100 text-gray-700',
};

function getExpiryStatus(expiresAt: string | null): 'valid' | 'expiring' | 'expired' | null {
  if (!expiresAt) return null;
  const now = Date.now();
  const expiry = new Date(expiresAt).getTime();
  if (expiry < now) return 'expired';
  if (expiry - now < 30 * 24 * 60 * 60 * 1000) return 'expiring';
  return 'valid';
}

const EXPIRY_STYLES: Record<string, string> = {
  valid: 'text-muted-foreground',
  expiring: 'text-amber-600 font-semibold',
  expired: 'text-red-600 font-semibold',
};

const EXPIRY_LABELS: Record<string, string> = {
  expiring: 'Próximo a vencer',
  expired: 'Vencido',
};

const AWARD_COLORS: Record<string, string> = {
  induction: 'bg-blue-100 text-blue-600',
  periodic: 'bg-purple-100 text-purple-600',
  document_change: 'bg-amber-100 text-amber-600',
  general: 'bg-gray-100 text-gray-600',
};

export function CertificatesPage() {
  const [page, setPage] = useState(1);
  const [positionId, setPositionId] = useState<number | undefined>(undefined);
  const categoryLabel = useCategoryLabel('course_category');

  const {
    data: certificatesRes,
    isLoading,
    error,
  } = useMyCertificates({
    page,
    per_page: 9,
    position_id: positionId,
  });

  const certificates = certificatesRes?.data ?? [];
  const pagination = certificatesRes?.meta?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Mis Certificados</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Certificados obtenidos por la aprobación de capacitaciones.
        </p>
      </div>

      <PositionFilter value={positionId} onChange={setPositionId} />

      {/* Content */}
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message="No se pudieron cargar los certificados." />
      ) : certificates.length === 0 ? (
        <EmptyState title="Sin certificados" description="No tienes certificados aún." />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {certificates.map((cert) => {
              const expiryStatus = getExpiryStatus(cert.expires_at);
              const category = cert.course?.category ?? 'general';
              const awardColor = AWARD_COLORS[category] ?? AWARD_COLORS.general;

              return (
                <div
                  key={cert.id}
                  className="flex flex-col rounded-xl border border-border bg-card transition-shadow hover:shadow-md"
                >
                  <div className="flex flex-1 flex-col p-5">
                    {/* Award icon */}
                    <div
                      className={cn(
                        'inline-flex h-10 w-10 items-center justify-center rounded-full',
                        awardColor,
                      )}
                    >
                      <Award className="h-5 w-5" />
                    </div>

                    {/* Course title */}
                    <h3 className="mt-3 line-clamp-2 text-base font-semibold text-card-foreground">
                      {cert.course?.title ?? cert.course_title}
                    </h3>

                    {/* Category badge */}
                    <div className="mt-2">
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          CATEGORY_COLORS[category] ?? CATEGORY_COLORS.general,
                        )}
                      >
                        {categoryLabel(category)}
                      </span>
                    </div>

                    {/* Certificate number */}
                    <p className="mt-3 font-mono text-xs text-muted-foreground">
                      {cert.certificate_number}
                    </p>

                    {/* Dates */}
                    <div className="mt-3 space-y-1 text-xs">
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Emitido: {formatDate(cert.issued_at)}</span>
                      </div>
                      {cert.expires_at && (
                        <div
                          className={cn(
                            'flex items-center gap-1.5',
                            expiryStatus ? EXPIRY_STYLES[expiryStatus] : 'text-muted-foreground',
                          )}
                        >
                          <Calendar className="h-3.5 w-3.5" />
                          <span>
                            Vence: {formatDate(cert.expires_at)}
                            {expiryStatus && expiryStatus !== 'valid' && (
                              <> - {EXPIRY_LABELS[expiryStatus]}</>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Download action */}
                  {cert.download_url && (
                    <div className="border-t border-border px-5 py-3">
                      <a
                        href={cert.download_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                      >
                        <Download className="h-4 w-4" />
                        Descargar
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {pagination && <Pagination meta={pagination} onPageChange={setPage} />}
        </>
      )}
    </div>
  );
}
